import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { GUIDE_HUB_PATH, LIST_PATHS } from '@/router/config';

export default function Navbar() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const pathname = location.pathname;

  const isHomeActive = pathname === '/';
  const isSuitActive = pathname === LIST_PATHS.SUIT || pathname.startsWith(`${LIST_PATHS.SUIT}/`);
  const isJewelryActive = pathname === LIST_PATHS.JEWELRY || pathname.startsWith(`${LIST_PATHS.JEWELRY}/`);
  const isHanbokActive = pathname === LIST_PATHS.HANBOK || pathname.startsWith(`${LIST_PATHS.HANBOK}/`);
  const isGuideActive = pathname === GUIDE_HUB_PATH || pathname.startsWith(`${GUIDE_HUB_PATH}/`);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setMobileMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen, handleKeyDown]);

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <>
      {/* ===== Desktop & Mobile Top Navbar ===== */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-static-white border-b border-line-divider">
        <div className="w-full px-5 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
              <img src='/resources/images/directwedding-logo.svg' style={{height: 40}} />
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-fill-normal text-accent-brown text-caption font-medium whitespace-nowrap">
                혼수 예약 센터
              </span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <Link
                to={LIST_PATHS.SUIT}
                className={`text-label1 font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  isSuitActive ? 'text-primary-normal' : 'text-label-alt hover:text-label-normal'
                }`}
              >
                예복
              </Link>
              <Link
                to={LIST_PATHS.JEWELRY}
                className={`text-label1 font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  isJewelryActive ? 'text-primary-normal' : 'text-label-alt hover:text-label-normal'
                }`}
              >
                예물
              </Link>
              <Link
                to={LIST_PATHS.HANBOK}
                className={`text-label1 font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  isHanbokActive ? 'text-primary-normal' : 'text-label-alt hover:text-label-normal'
                }`}
              >
                한복
              </Link>
            </div>

            {/* Desktop CTA + Mobile Hamburger */}
            <div className="flex items-center gap-3">
              <Link
                to={GUIDE_HUB_PATH}
                className={`hidden md:inline-flex items-center h-9 px-5 rounded-full text-label1 font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  isGuideActive
                    ? 'bg-primary-strong text-static-white'
                    : 'bg-primary-normal text-static-white hover:bg-primary-strong'
                }`}
              >
                내 취향 찾기
                <i className="ri-arrow-right-line ml-1.5 text-sm"></i>
              </Link>
              <button
                className="md:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-fill-alt transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="메뉴 열기"
              >
                <i className="ri-menu-line text-label-normal text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ===== Mobile Drawer Dim ===== */}
      <div
        className={`fixed inset-0 z-40 md:hidden bg-black/35 cursor-pointer ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ transition: 'opacity 200ms ease' }}
        onClick={closeMenu}
      />

      {/* ===== Mobile Drawer Panel ===== */}
      <div
        className="fixed top-0 right-0 z-50 h-full md:hidden bg-static-white flex flex-col"
        style={{
          width: 'min(84vw, 360px)',
          height: '100dvh',
          transition: 'transform 220ms ease-out',
          transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
          boxShadow: mobileMenuOpen ? '-2px 0 16px rgba(0,0,0,0.06)' : 'none',
        }}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between h-16 px-5 border-b border-line-divider flex-shrink-0">
          <span className="text-headline2 text-label-normal font-semibold">다이렉트 인증업체</span>
          <button
            onClick={closeMenu}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-fill-alt transition-colors cursor-pointer"
            aria-label="메뉴 닫기"
          >
            <i className="ri-close-line text-label-normal text-xl"></i>
          </button>
        </div>

        {/* Drawer Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-5 pt-6 pb-4">
            <Link
              to="/"
              onClick={closeMenu}
              className={`flex items-center h-12 px-3 rounded-lg text-label1 font-medium transition-colors cursor-pointer ${
                isHomeActive
                  ? 'bg-fill-normal text-accent-brown'
                  : 'text-label-neutral hover:bg-fill-alt'
              }`}
            >
              <i className="ri-home-4-line text-lg mr-3"></i>
              홈
            </Link>

            <p className="text-caption text-label-assistive font-medium mt-7 mb-2 px-3 tracking-wide">
              인증업체
            </p>

            <Link
              to={LIST_PATHS.SUIT}
              onClick={closeMenu}
              className={`flex items-center h-12 px-3 rounded-lg text-label1 transition-colors cursor-pointer ${
                isSuitActive
                  ? 'bg-fill-normal text-accent-brown font-medium'
                  : 'text-label-neutral hover:bg-fill-alt'
              }`}
            >
              <span className="w-7 flex-shrink-0 text-sm mr-1">
                <i className="ri-checkbox-circle-line text-accent-brown"></i>
              </span>
              예복
            </Link>

            <Link
              to={LIST_PATHS.JEWELRY}
              onClick={closeMenu}
              className={`flex items-center h-12 px-3 rounded-lg text-label1 transition-colors cursor-pointer ${
                isJewelryActive
                  ? 'bg-fill-normal text-accent-brown font-medium'
                  : 'text-label-neutral hover:bg-fill-alt'
              }`}
            >
              <span className="w-7 flex-shrink-0 text-sm mr-1">
                <i className="ri-checkbox-circle-line text-accent-brown"></i>
              </span>
              예물
            </Link>

            <Link
              to={LIST_PATHS.HANBOK}
              onClick={closeMenu}
              className={`flex items-center h-12 px-3 rounded-lg text-label1 transition-colors cursor-pointer ${
                isHanbokActive
                  ? 'bg-fill-normal text-accent-brown font-medium'
                  : 'text-label-neutral hover:bg-fill-alt'
              }`}
            >
              <span className="w-7 flex-shrink-0 text-sm mr-1">
                <i className="ri-checkbox-circle-line text-accent-brown"></i>
              </span>
              한복
            </Link>

            <p className="text-caption text-label-assistive font-medium mt-7 mb-2 px-3 tracking-wide">
              내 취향 찾기
            </p>

            <Link
              to="/guide/suit"
              onClick={closeMenu}
              className={`flex items-center h-12 px-3 rounded-lg text-label1 transition-colors cursor-pointer ${
                pathname === '/guide/suit' || pathname.startsWith('/guide/suit/')
                  ? 'bg-fill-normal text-accent-brown font-medium'
                  : 'text-label-neutral hover:bg-fill-alt'
              }`}
            >
              <span className="w-7 flex-shrink-0 text-sm mr-1">
                <i className="ri-compass-3-line text-label-alt"></i>
              </span>
              예복 취향 찾기
            </Link>

            <Link
              to="/guide/jewelry"
              onClick={closeMenu}
              className={`flex items-center h-12 px-3 rounded-lg text-label1 transition-colors cursor-pointer ${
                pathname === '/guide/jewelry' || pathname.startsWith('/guide/jewelry/')
                  ? 'bg-fill-normal text-accent-brown font-medium'
                  : 'text-label-neutral hover:bg-fill-alt'
              }`}
            >
              <span className="w-7 flex-shrink-0 text-sm mr-1">
                <i className="ri-compass-3-line text-label-alt"></i>
              </span>
              예물 취향 찾기
            </Link>

            <Link
              to="/guide/hanbok"
              onClick={closeMenu}
              className={`flex items-center h-12 px-3 rounded-lg text-label1 transition-colors cursor-pointer ${
                pathname === '/guide/hanbok' || pathname.startsWith('/guide/hanbok/')
                  ? 'bg-fill-normal text-accent-brown font-medium'
                  : 'text-label-neutral hover:bg-fill-alt'
              }`}
            >
              <span className="w-7 flex-shrink-0 text-sm mr-1">
                <i className="ri-compass-3-line text-label-alt"></i>
              </span>
              한복 취향 찾기
            </Link>
          </div>
        </div>

        {/* Drawer Bottom CTA */}
        <div className="px-5 pb-8 pt-3 border-t border-line-alt flex-shrink-0">
          <Link
            to={GUIDE_HUB_PATH}
            onClick={closeMenu}
            className="flex items-center justify-center h-12 rounded-full bg-primary-normal text-static-white text-label1 font-semibold hover:bg-primary-strong transition-colors cursor-pointer whitespace-nowrap"
          >
            내 취향 찾기 시작
            <i className="ri-arrow-right-line ml-1.5"></i>
          </Link>
        </div>
      </div>
    </>
  );
}
